// @ts-nocheck
// CAMINHO: src/components/cases/CaseDetailModal.tsx (VERSÃO FINAL E CORRIGIDA)

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BookOpen, Sparkles, Loader2, Users, GanttChartSquare, RefreshCcw, ExternalLink, Calendar } from 'lucide-react';
import { CaseTimeline } from '@/components/CaseTimeline';
import { AdvancedAIChat } from '@/components/AdvancedAIChat';
import { Case } from '@/types/case';
import type { ProcessData } from "@/types/datajud";
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ActivitiesTab } from './ActivitiesTab';

// Função para gerar o link de consulta pública
const getPublicUrlForProcess = (case_: Case): string => {
    const numeroLimpo = (case_.case_number_cnj || case_.case_number || '').replace(/\D/g, '');
    if (!numeroLimpo) return '#';
    const tribunal = case_.court || case_.datajud_json?.tribunal || '';
    return `https://www.google.com/search?q=${numeroLimpo}+${tribunal}`;
}

interface CaseDetailModalProps {
  case_: Case | null;
  isOpen: boolean;
  onClose: () => void;
  onSync?: (caseId: string) => Promise<void>;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  aiAnalysis: string;
  aiLoading: boolean;
  onAnalyzeCase: (case_: Case) => void;
}

export const CaseDetailModal = ({
  case_,
  isOpen,
  onClose,
  onSync,
  activeTab,
  setActiveTab,
  aiAnalysis,
  aiLoading,
  onAnalyzeCase
}: CaseDetailModalProps) => {
  if (!case_) return null;

  const datajudData = case_.datajud_json as ProcessData | undefined;
  const hasDatajudData = !!datajudData;

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "Alta": return "bg-destructive text-destructive-foreground";
      case "Média": return "bg-yellow-500 text-yellow-foreground";
      case "Baixa": return "bg-secondary text-secondary-foreground";
      default: return "bg-muted text-muted-foreground";
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case "Concluído": return "bg-green-600 text-white";
      case "Em andamento": return "bg-primary text-primary-foreground";
      case "Aguardando documentos": return "bg-yellow-500 text-yellow-foreground";
      default: return "bg-muted text-muted-foreground";
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md w-full h-[90vh] flex flex-col bg-white shadow-2xl border border-gray-200">
        <DialogHeader className="flex flex-row justify-between items-center pr-6 pt-6 pb-6 bg-gradient-to-r from-blue-50 to-indigo-50 -m-6 mb-6 border-b border-blue-200">
          <DialogTitle className="flex items-center gap-3 text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-800 to-indigo-800 drop-shadow-lg">
            <BookOpen className="w-6 h-6" />
            {case_.title}
          </DialogTitle>
          {hasDatajudData && onSync && (
             <Button variant="outline" size="sm" onClick={() => onSync(case_!.id)}>
                <RefreshCcw className="w-4 h-4 mr-2"/>
                Atualizar com Datajud
             </Button>
          )}
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-y-auto pr-2">
          <TabsList className="grid w-full grid-cols-3 md:grid-cols-6 bg-muted sticky top-0 z-10">
            <TabsTrigger value="overview">Visão Geral</TabsTrigger>
            {hasDatajudData && <TabsTrigger value="partes">Partes</TabsTrigger>}
            {hasDatajudData && <TabsTrigger value="andamentos">Andamentos</TabsTrigger>}
            <TabsTrigger value="timeline">Timeline Interna</TabsTrigger>
            <TabsTrigger value="activities">Atividades</TabsTrigger>
            <TabsTrigger value="ai-analysis">Análise IA</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-white border border-[#c4ab83]/30"><CardHeader><CardTitle className="text-lg">Informações do Processo</CardTitle></CardHeader><CardContent className="space-y-3">
                <div><Label className="text-sm font-medium">Número CNJ:</Label><p>{case_.case_number_cnj || case_.case_number || 'Não informado'}</p></div>
                <div><Label className="text-sm font-medium">Tipo:</Label><Badge variant="outline">{case_.case_type}</Badge></div>
                <div><Label className="text-sm font-medium">Status:</Label><Badge className={getStatusColor(case_.status)}>{case_.status}</Badge></div>
                <div><Label className="text-sm font-medium">Prioridade:</Label><Badge className={getPriorityColor(case_.priority)}>{case_.priority}</Badge></div>
                {case_.deadline && (<div><Label className="text-sm font-medium">Prazo:</Label><p>{new Date(case_.deadline).toLocaleDateString('pt-BR')}</p></div>)}
              </CardContent></Card>
              <Card className="bg-white border border-[#c4ab83]/30"><CardHeader><CardTitle className="text-lg">Cliente e Responsável</CardTitle></CardHeader><CardContent className="space-y-3">
                {case_.clients && (<div><Label className="text-sm font-medium">Cliente:</Label><p>{case_.clients.name}</p><p className="text-sm text-muted-foreground">{case_.clients.email}</p></div>)}
                {case_.lawyer_responsible && (<div><Label className="text-sm font-medium">Advogado Responsável:</Label><p>{case_.lawyer_responsible}</p></div>)}
              </CardContent></Card>
            </div>
            {case_.description && (<Card className="bg-white border border-[#c4ab83]/30"><CardHeader><CardTitle className="text-lg">Descrição</CardTitle></CardHeader><CardContent><p className="whitespace-pre-wrap">{case_.description}</p></CardContent></Card>)}
          </TabsContent>

          {hasDatajudData && (
            <TabsContent value="partes" className="mt-4">
              <Card className="bg-white border border-[#c4ab83]/30"><CardHeader><CardTitle className="flex items-center gap-2 text-base"><Users className="w-5 h-5"/>Partes (via Datajud)</CardTitle></CardHeader><CardContent className="space-y-4">
                  {datajudData.partes && datajudData.partes.length > 0 ? (<>
                    <div><h4 className="font-semibold mb-2 border-b pb-1">Polo Ativo</h4>{datajudData.partes.filter((p: any) => p.polo === 'ATIVO').map((parte: any, index: number) => (<div key={index} className="text-sm py-1"><p>{parte.nome}</p><p className="text-xs text-muted-foreground">{parte.tipo}</p></div>))}
</div>
                    <div><h4 className="font-semibold mb-2 border-b pb-1">Polo Passivo</h4>{datajudData.partes.filter((p: any) => p.polo === 'PASSIVO').map((parte: any, index: number) => (<div key={index} className="text-sm py-1"><p>{parte.nome}</p><p className="text-xs text-muted-foreground">{parte.tipo}</p></div>))}
</div>
                  </>) : <p className="text-sm text-muted-foreground">Dados de partes não disponíveis.</p>}
              </CardContent></Card>
            </TabsContent>
          )}

          {hasDatajudData && (
            <TabsContent value="andamentos" className="mt-4">
              <Card className="bg-white border border-[#c4ab83]/30"><CardHeader><CardTitle className="flex items-center gap-2 text-base"><GanttChartSquare className="w-5 h-5"/>Andamentos (via Datajud)</CardTitle></CardHeader><CardContent className="space-y-3 max-h-[calc(90vh-300px)] overflow-y-auto">
                  {datajudData.movimentacoes && datajudData.movimentacoes.length > 0 ? (
                    datajudData.movimentacoes.map((mov: any, index: number) => (
                      <div key={index} className="text-sm border-b pb-2 last:border-b-0">
                        <div className="flex justify-between items-start gap-2">
                          <p className="font-semibold flex-1">{mov.nome}</p>
                          <a href={getPublicUrlForProcess(case_)} target="_blank" rel="noopener noreferrer"><Button variant="ghost" size="sm" className="h-7 text-xs px-2"><ExternalLink className="w-3 h-3 mr-1" />Ver no Tribunal</Button></a>
                        </div>
                        <p className="text-xs text-muted-foreground">{format(new Date(mov.dataHora), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</p>
                        {mov.complemento && <p className="text-sm mt-1 bg-slate-50 p-2 rounded border">{mov.complemento}</p>}
                      </div>
                    ))
                  ) : <p className="text-sm text-muted-foreground">Nenhuma movimentação encontrada nos dados salvos.</p>}
              </CardContent></Card>
            </TabsContent>
          )}

          <TabsContent value="timeline"><CaseTimeline caseId={case_.id} /></TabsContent>

          <TabsContent value="activities" className="mt-4">
            <ActivitiesTab caseId={case_.id} />
          </TabsContent>

          <TabsContent value="ai-analysis" className="mt-4 space-y-4">
            {aiAnalysis ? (<Card className="bg-white border border-[#c4ab83]/30"><CardHeader><CardTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5" />Análise Inteligente</CardTitle></CardHeader><CardContent><div className="whitespace-pre-wrap">{aiAnalysis}</div></CardContent></Card>) : (<div className="text-center py-8">{aiLoading ? (<div className="flex items-center justify-center gap-2"><Loader2 className="w-6 h-6 animate-spin" /><span>Analisando...</span></div>) : (<Button onClick={() => onAnalyzeCase(case_)}><Sparkles className="w-4 h-4 mr-2" />Gerar Análise com IA</Button>)}</div>)}
            <AdvancedAIChat initialTaskType="case_analysis" title="Consultoria Jurídica Avançada" placeholder="Faça perguntas específicas sobre este processo..." context={`Processo: ${case_.title}\nTipo: ${case_.case_type}\nStatus: ${case_.status}\nDescrição: ${case_.description}\nCliente: ${case_.clients?.name}`}/>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};